import { useEffect, useState } from 'react';
import { checkoutOffers, creditBalance, startCheckout, type CheckoutKind, type CheckoutOffers, type CreditBalance } from '../api/billing';
import { PRIVACY_URL, TERMS_URL } from '@/config/legal';
import {
  checkoutOffered,
  offerButton,
  offerFor,
  offerTerms,
  rememberCheckoutOffer,
  trialCheckoutAwaited,
  type Offer,
} from '../state/trial';
import { useSession } from '../state/useSession';
import { Button, Screen, SectionLabel } from '../ui';
import RedeemCode, { type Redeemed } from './account/RedeemCode';

/** What one top-up adds, when the server's answer leaves it out. The server's table decides. */
const TOPUP_CREDITS = 10000;

/**
 * The browser buys credits through Stripe Checkout, never RevenueCat.
 *
 * src/payments/webCheckout.ts was written for exactly this client — its header names
 * app.saddle-sites.com — because react-native-purchases is a native module and an IAP
 * button in a browser is a dead end. This also keeps the web builder outside the app's
 * in-app-purchase surface entirely.
 */
export default function Credits() {
  const [credits, setCredits] = useState<number | null>(null);
  const [tier, setTier] = useState<string>('free');
  /*
   * Whether a store is BILLING this account — the server's `subscribed`, never the tier. A promo
   * code (CREDITS4MARTI: 1,000 credits and the paid tier) sets the tier with no plan behind it,
   * and deciding on the tier offered those people the subscribers' top-up, or told them their
   * credits "refill when your plan renews", and never the plan itself.
   */
  const [subscribed, setSubscribed] = useState(false);
  /*
   * The trial, or — one per account (the server, 2026-09-29) — the plan from day one. "Start 7-day
   * free trial" over a checkout that charges $20 on the spot promised a week the account had had.
   */
  const [offer, setOffer] = useState<Offer>('trial');
  /*
   * Whether the server SAID the trial is still this account's — true only on its yes. `offer`
   * cannot stand in for it: it is 'trial' until the balance says otherwise, and 'trial' for a
   * balance that says nothing, so a refusal of the top-up promised a used trial (review of F23,
   * 2026-09-30). Unknown is no.
   */
  const [trialAvailable, setTrialAvailable] = useState(false);
  const known = credits !== null;
  /*
   * Whether the balance question has been answered, either way. Nothing is offered for sale
   * until it has: a subscriber must not be shown Subscribe for the moment before their tier
   * arrives — that button is how they used to buy a second plan.
   */
  const [settled, setSettled] = useState(false);
  const [offers, setOffers] = useState<CheckoutOffers>({ topup: { available: false } });
  /*
   * And whether what is on sale has been answered. The subscriber's row is a button or a line
   * depending on it, and drawn before the answer it showed "refill when your plan renews" and
   * then swapped it for "Buy 10,000 credits".
   */
  const [offersKnown, setOffersKnown] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const ownerId = useSession().user?.id ?? '';
  /*
   * Back from Stripe with a trial the account does not show yet (state/trial.ts). The success page
   * says the credits "can take a minute" and links here; in that minute this screen still read the
   * free tier and offered the trial again, and one press made a second one. Decided from the
   * record before the balance answers too: a read that fails must not offer it either.
   */
  const [awaited, setAwaited] = useState(() => trialCheckoutAwaited(ownerId, {}) !== null);

  function show(b: CreditBalance): void {
    setCredits(b.creditsRemaining);
    setTier(b.tier);
    setSubscribed(b.subscribed === true);
    setOffer(offerFor(b));
    setTrialAvailable(b.trialAvailable === true);
    setAwaited(trialCheckoutAwaited(ownerId, b) !== null);
  }

  /** Asked again by hand while the account catches up with a trial. */
  async function checkAgain(): Promise<void> {
    setBusy(true);
    try {
      await creditBalance().then(show).catch(() => undefined);
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    /*
     * Ask what the balance actually IS. A store starting at zero would tell someone they
     * have none before they had spent anything — which is what the phone used to do.
     * A failure leaves it unknown, and unknown shows "—" rather than a wrong number.
     */
    void creditBalance()
      .then(show)
      .catch(() => undefined)
      .finally(() => setSettled(true));
    // Whether a top-up is on sale at all; "no" until the server says otherwise.
    void checkoutOffers()
      .then(setOffers)
      .finally(() => setOffersKnown(true));
  }, []);

  /** A code's answer carries the new balance, so the number above changes without a reload. */
  function redeemed(result: Redeemed): void {
    if (typeof result.creditsRemaining === 'number') setCredits(result.creditsRemaining);
    if (result.tier) setTier(result.tier);
  }

  async function buy(kind: CheckoutKind): Promise<void> {
    setError(null);
    setBusy(true);
    try {
      // Which offer this was, for the page Stripe sends them back to (state/trial.ts).
      if (kind === 'subscription') rememberCheckoutOffer(offer);
      // Whether the trial may be named if a top-up is refused for want of a plan (F23).
      const result = kind === 'topup'
        ? await startCheckout(kind, undefined, { trialAvailable })
        : await startCheckout(kind);
      if (result.status === 'signin_required') setError('Please sign in before buying credits.');
      else if (result.status === 'error') {
        setError(result.message);
        /*
         * A subscriber from before the server recorded which store bills them reads as not
         * subscribed until the server asks Stripe, which it does exactly here — and remembers. So
         * the refusal is also the answer: read the balance again, and the top-up takes the place
         * of the Subscribe button that was just refused.
         */
        if (result.reason === 'already_subscribed') {
          await creditBalance().then(show).catch(() => undefined);
        }
      }
      // 'redirecting' navigates the browser away; nothing left to do.
    } finally {
      setBusy(false);
    }
  }

  return (
    <Screen title="Credits">
      <p className="type-display credits-balance">
        {tier === 'unlimited' ? 'Unlimited' : known ? credits : '—'}
      </p>
      <p className="type-body project-meta">
        {tier === 'unlimited'
          ? 'This account has no limit.'
          : 'Credits are spent when Saddle builds or changes an app.'}
      </p>

      {error && <p role="alert" className="type-caption field-error-text">{error}</p>}

      {/*
        One thing to buy, and which one depends on who is buying. "Get more credits" sold
        everybody the subscription, so a subscriber whose month ran out bought a SECOND $20
        plan. A subscriber — somebody a store is billing, not merely the paid tier — is offered
        the one-time top-up, only while the server says it is on sale, and told plainly
        otherwise. Everybody else, the promo's paid tier included, is offered the trial; a
        balance that could not be read offers it too, and the server refuses a subscriber a
        second plan and says so, in words.
      */}
      {/*
        The free tier is gone (the owner, 2026-09-25), and every subscription checkout the server
        makes is the 7-day trial with the card taken up front — the same one the Plan screen's
        Build button opens. So it is offered as what it is, with its terms beside it.
      */}
      {settled && tier !== 'unlimited' && !subscribed && !awaited && (
        <>
          <p className="type-caption project-meta">{`${offerTerms(offer).headline} ${offerTerms(offer).detail}`}</p>
          <Button title={offerButton(offer)} onPress={() => void buy('subscription')} loading={busy} />
        </>
      )}
      {settled && tier !== 'unlimited' && !subscribed && awaited && (
        <>
          <p role="status" className="type-caption project-meta">
            Stripe has your {checkoutOffered() === 'subscribe' ? 'subscription' : 'trial'}. It shows here once your account catches up with it — usually within a minute.
          </p>
          <Button title="Check again" variant="secondary" onPress={() => void checkAgain()} loading={busy} />
        </>
      )}
      {settled && offersKnown && tier !== 'unlimited' && subscribed && offers.topup.available && (
        <Button
          title={`Buy ${(offers.topup.credits ?? TOPUP_CREDITS).toLocaleString()} credits`}
          onPress={() => void buy('topup')}
          loading={busy}
        />
      )}
      {settled && offersKnown && tier !== 'unlimited' && subscribed && !offers.topup.available && (
        <p className="type-caption project-meta">Your 20,000 credits refill when your plan renews.</p>
      )}

      {/* The phone's Account → Credits field. On the web it sits here too, next to the balance
          it changes: the iOS app no longer offers it (App Store 3.1.1), so a code given to
          somebody on an iPhone is redeemed in a browser. */}
      <SectionLabel>Redeem code</SectionLabel>
      <RedeemCode tier={known ? tier : null} onRedeemed={redeemed} />

      <p className="type-caption legal-links">
        <a href={TERMS_URL} target="_blank" rel="noreferrer">Terms</a>
        {' · '}
        <a href={PRIVACY_URL} target="_blank" rel="noreferrer">Privacy Policy</a>
      </p>
    </Screen>
  );
}
